// src/api/files.ts
//
// Axios client for /api/v2/file/* endpoints (secrets-files fork).

import type { AxiosInstance } from 'axios'

export interface UploadFilePayload {
  iv_b64: string
  salt_b64: string
  content_base64: string
  ttl?: number
  mime_type_hint?: string
}

export interface UploadFileResponse {
  success: boolean
  record: {
    extid: string
    shortid: string
    size_bytes: number
    mime_type_hint: string | null
    lifespan: number
    expires_at: number | null
    created: number
  }
}

export interface FileMetadataResponse {
  extid: string
  size_bytes: number
  mime_type_hint: string | null
  expires_at: number | null
  state: 'new' | 'viewed' | 'burned'
  /** Presente si es un fichero chunked (Fase 5). */
  chunked?: boolean
  total_chunks?: string | null
}

export interface DownloadFileResponse {
  extid: string
  iv_b64: string
  salt_b64: string
  ciphertext_b64: string
  mime_type_hint: string | null
  size_bytes: number
}

export function uploadEncryptedFile(
  api: AxiosInstance,
  payload: UploadFilePayload,
): Promise<UploadFileResponse> {
  return api.post('/api/v2/file', payload).then((r) => r.data)
}

export function fetchFileMetadata(
  api: AxiosInstance,
  identifier: string,
): Promise<FileMetadataResponse> {
  return api.get(`/api/v2/file/${encodeURIComponent(identifier)}/metadata`).then((r) => r.data)
}

export function downloadEncryptedFile(
  api: AxiosInstance,
  identifier: string,
): Promise<DownloadFileResponse> {
  return api.get(`/api/v2/file/${encodeURIComponent(identifier)}`).then((r) => r.data)
}

export function burnFile(
  api: AxiosInstance,
  identifier: string,
): Promise<{ extid: string; burned: boolean }> {
  return api
    .post(`/api/v2/file/${encodeURIComponent(identifier)}/burn`)
    .then((r) => r.data)
}

// ---------------------------------------------------------------------------
// API chunked (Fase 5): init → chunk×N → finalize + manifest + download_chunk
// ---------------------------------------------------------------------------

export interface InitChunkedResponse {
  success: boolean
  extid: string
  upload_token: string
  total_chunks: string
  chunk_size: string
  expires_at: number | null
}

export interface ChunkedManifestResponse {
  extid: string
  chunked: boolean
  total_chunks: string
  chunk_size: string
  salt_b64: string
  base_nonce_b64: string
  size_bytes: number
  mime_type_hint: string | null
  download_token: string
}

export interface FinalizeChunkedResponse {
  success: boolean
  extid: string
  size_bytes: number
  expires_at: number | null
}

export function initChunkedUpload(
  api: AxiosInstance,
  payload: {
    salt_b64: string
    base_nonce_b64: string
    total_chunks: number
    chunk_size: number
    total_size: number
    ttl?: number
    mime_type_hint?: string
  },
): Promise<InitChunkedResponse> {
  return api.post('/api/v2/file/chunked', payload).then((r) => r.data)
}

export function putChunk(
  api: AxiosInstance,
  extid: string,
  index: number,
  contentB64: string,
  uploadToken: string,
): Promise<{ received: number }> {
  return api
    .put(`/api/v2/file/${encodeURIComponent(extid)}/chunk/${index}`, {
      content_base64: contentB64,
      upload_token: uploadToken,
    })
    .then((r) => r.data)
}

export function finalizeChunkedUpload(
  api: AxiosInstance,
  extid: string,
  uploadToken: string,
): Promise<FinalizeChunkedResponse> {
  return api
    .post(`/api/v2/file/${encodeURIComponent(extid)}/finalize`, {
      upload_token: uploadToken,
    })
    .then((r) => r.data)
}

export function fetchManifest(
  api: AxiosInstance,
  extid: string,
): Promise<ChunkedManifestResponse> {
  return api.get(`/api/v2/file/${encodeURIComponent(extid)}/manifest`).then((r) => r.data)
}

export function downloadChunk(
  api: AxiosInstance,
  extid: string,
  index: number,
  downloadToken: string,
): Promise<{ content_base64: string }> {
  return api
    .get(`/api/v2/file/${encodeURIComponent(extid)}/chunk/${index}`, {
      params: { download_token: downloadToken },
    })
    .then((r) => r.data)
}
